/**
 * One task, with its actions wired: `LaneV2TaskPanel` (stepper → plan → build → package → validation)
 * plus cancel / revert / save handlers and the one-line outcome of the last action.
 *
 * Mounted in two places for one reason: beside the ask that is ABOUT this task (the approver must see
 * what they approve), and in the workspace's Task section for every other task. Same component, so
 * the two never disagree about what a task looks like.
 */
import { useState } from "react";
import { describeLaneV2Error, isLaneV2Error } from "@/services/multi-agent/laneV2/api";
import { cancelRefusalCopy } from "@/services/multi-agent/laneV2/conversation";
import type { SaveBranchIn, SaveBranchOut, TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { LaneV2TaskPanel } from "./LaneV2TaskPanel";

export interface LaneV2TaskActions {
  onCancelTask: (taskId: string) => Promise<{ cancelled: boolean; reason: string | null }>;
  onRevertTask: (taskId: string) => Promise<void>;
  /** Present only when R0 advertises `branch_save` — omitted, `PackageView` shows no save affordance. */
  onSaveBranch?: (taskId: string, body: SaveBranchIn) => Promise<SaveBranchOut>;
  /** Re-run the check against the org with a fresh idempotency key (contracts 0.2.30). */
  onValidateTask?: (taskId: string, idempotencyKey: string) => Promise<unknown>;
  onViewItemBody: (taskId: string, workItemId: string) => void;
}

export interface LaneV2TaskBlockProps extends LaneV2TaskActions {
  snapshot: TaskSnapshot;
}

export function LaneV2TaskBlock({ snapshot, onCancelTask, onRevertTask, onViewItemBody }: LaneV2TaskBlockProps) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const run = async (fn: () => Promise<string | null>) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      setMessage(await fn());
    } catch (err) {
      setMessage(isLaneV2Error(err) ? describeLaneV2Error(err) : "That did not work. Nothing was changed. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-lane-v2-task={snapshot.task_id}>
      <LaneV2TaskPanel
        snapshot={snapshot}
        busy={busy}
        onViewItemBody={(workItemId) => onViewItemBody(snapshot.task_id, workItemId)}
        onCancelTask={() =>
          void run(async () => {
            const out = await onCancelTask(snapshot.task_id);
            return out.cancelled ? null : cancelRefusalCopy(out.reason);
          })
        }
        onRevert={
          snapshot.state === "applied"
            ? () =>
                void run(async () => {
                  await onRevertTask(snapshot.task_id);
                  return "A revert task was opened. Confirm it below to deploy the inverse change.";
                })
            : undefined
        }
      />
      {message && (
        <p role="status" className="mt-1.5 text-[12px] text-muted-foreground">
          {message}
        </p>
      )}
    </div>
  );
}
